import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AppliedInvoice, MatchedInvoice, MatchMethod } from '@dill/dill-shared';
import { formatCurrency } from '../utils';

export const MATCH_METHOD_LABELS: Record<MatchMethod, { label: string; className: string }> = {
  EXACT_DOC_NUMBER: { label: 'Exact Doc#', className: 'bg-emerald-50 text-emerald-700' },
  EXACT_DOC_NUMBER_AMOUNT: { label: 'Exact Match', className: 'bg-emerald-100 text-emerald-800' },
  EXACT_PO: { label: 'Exact PO', className: 'bg-blue-50 text-blue-700' },
  EXACT_JOB_NAME: { label: 'Exact Job', className: 'bg-emerald-50 text-emerald-700' },
  EXACT_AMOUNT: { label: 'Exact Amt', className: 'bg-cyan-50 text-cyan-700' },
  FUZZY: { label: 'Fuzzy', className: 'bg-gray-100 text-gray-600' },
  VECTOR_SEARCH: { label: 'AI', className: 'bg-violet-50 text-violet-700' },
  // Grey, not green: the advice named no invoice and we chose these ourselves.
  LAST_STATEMENT: { label: 'From statement', className: 'bg-gray-100 text-gray-600' },
  OPEN_INVOICES: { label: 'Oldest open', className: 'bg-gray-100 text-gray-600' },
  // Amber, and not styled like the exact matches it used to be recorded as: a person decided this,
  // and the reviewer should see that rather than a score Dill would be taking credit for.
  MANUAL: { label: 'Manual', className: 'bg-amber-50 text-amber-700' },
};

/**
 * Portal-based hover tooltip for matched invoice details.
 *
 * Shared by every queue that shows a proposed allocation: the invoices, the amount going to
 * each, and how each one was matched are the same answer to "what will this payment do"
 * wherever the record happens to be waiting.
 */
export const MatchedInvoicesTooltip = ({
  invoices,
  pendingAllocs,
  children,
}: {
  invoices: MatchedInvoice[];
  pendingAllocs?: AppliedInvoice[];
  children: (props: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    ref: React.RefObject<HTMLDivElement>;
  }) => React.ReactNode;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleTriggerMouseEnter = () => {
    clearHideTimeout();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const PANEL_W = 520;
      const left =
        rect.right + PANEL_W + 8 > window.innerWidth ? rect.left - PANEL_W - 4 : rect.right + 8;
      setPosition({ top: rect.top, left });
    }
    setIsHovered(true);
  };

  const handleTooltipMouseEnter = () => clearHideTimeout();

  const handleMouseLeave = () => {
    clearHideTimeout();
    timeoutRef.current = setTimeout(() => setIsHovered(false), 150);
  };

  useEffect(() => {
    return () => clearHideTimeout();
  }, []);

  /**
   * Pull the panel back inside the viewport once its real height is known.
   *
   * Anchoring to the row's top is right near the top of the table and wrong for the last rows,
   * where the invoice list runs off the bottom of the window. The panel is a hover target with no
   * scroll of its own at that point, so the invoices below the fold simply cannot be read — which
   * is exactly how an eight-line advice came to look like a five-line one.
   */
  useLayoutEffect(() => {
    if (!isHovered || !panelRef.current) {
      return;
    }
    const { height } = panelRef.current.getBoundingClientRect();
    const VIEWPORT_MARGIN = 8;
    const lowestTop = window.innerHeight - height - VIEWPORT_MARGIN;
    setPosition((current) => {
      const top = Math.max(VIEWPORT_MARGIN, Math.min(current.top, lowestTop));
      return top === current.top ? current : { ...current, top };
    });
  }, [isHovered]);

  const displayInvoices = pendingAllocs?.length
    ? invoices.map((inv) => {
        const pendingInv = pendingAllocs.find((p) => p.invoiceId === inv.invoiceId);
        return pendingInv ? { ...inv, suggestedAmount: pendingInv.amountApplied } : inv;
      })
    : invoices;

  return (
    <>
      {children({
        onMouseEnter: handleTriggerMouseEnter,
        onMouseLeave: handleMouseLeave,
        ref: triggerRef,
      })}
      {isHovered &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed bg-white border border-gray-200 shadow-lg rounded-xl z-[99999] w-[520px]"
            style={{ top: `${position.top}px`, left: `${position.left}px` }}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => e.stopPropagation()}>
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Matched Invoices ({displayInvoices.length})
              </p>
            </div>
            <div className="max-h-[280px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                    <th className="text-left px-3 py-1.5">Doc #</th>
                    <th className="text-right px-3 py-1.5">Invoice</th>
                    <th className="text-right px-3 py-1.5">Balance</th>
                    <th className="text-right px-3 py-1.5">Suggested</th>
                    <th className="text-center px-3 py-1.5">Confidence</th>
                    <th className="text-left px-3 py-1.5">Method</th>
                  </tr>
                </thead>
                <tbody>
                  {displayInvoices.map((inv) => {
                    const mc = MATCH_METHOD_LABELS[inv.matchMethod] ?? MATCH_METHOD_LABELS.FUZZY;
                    return (
                      <tr
                        key={inv.invoiceId}
                        className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                        <td className="px-3 py-1.5 font-medium text-gray-900">{inv.docNumber}</td>
                        <td className="px-3 py-1.5 text-right text-gray-600">
                          {formatCurrency(inv.invoiceAmount)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-600">
                          {formatCurrency(inv.openBalance)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold text-gray-900">
                          {formatCurrency(inv.suggestedAmount ?? 0)}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <span
                            className={`text-[11px] font-medium ${
                              inv.confidence >= 0.75
                                ? 'text-emerald-600'
                                : inv.confidence >= 0.5
                                ? 'text-amber-600'
                                : 'text-red-500'
                            }`}>
                            {Math.round(inv.confidence * 100)}%
                          </span>
                        </td>
                        <td className="px-3 py-1.5">
                          <span
                            className={
                              'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ' +
                              mc.className
                            }>
                            {mc.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
